import { useApiClient } from '@frontend-common/api-client-provider';
import { useTreatyQuery } from '@frontend-common/utils/use-treaty-query';
import { useMemo } from 'react';
import { useMachineStatusList } from './use-machine-status-list';

export function useShopDetails(
  shopId: number | null,
  opts?: { throwOnError?: boolean; refetchInterval?: number; staleTime?: number; couponNonceKey?: string },
) {
  const client = useApiClient();
  const shopDetailsQuery = useTreatyQuery({
    queryKey: ['shopDetails', shopId],
    throwOnError: opts?.throwOnError,
    queryFn: async () => client.shops({ id: shopId! }).get({ query: { couponNonceKey: opts?.couponNonceKey } }),
    retry: false,
    enabled: !!shopId,
  });

  const machineStatusList = useMachineStatusList(shopId, opts);
  const isMachineStatesPending =
    machineStatusList.isPending || (machineStatusList.isFetching && machineStatusList.isStale);

  return useMemo(() => {
    if (!shopDetailsQuery.data) {
      return { ...shopDetailsQuery, isMachineStatesPending };
    }
    const machineMap = Object.fromEntries(machineStatusList.data?.map(status => [status.machineId, status]) ?? []);

    return {
      ...shopDetailsQuery,
      isMachineStatesPending,
      isPending: shopDetailsQuery.isPending,
      data: {
        ...shopDetailsQuery.data,
        machines: {
          washingMachines: shopDetailsQuery.data.machines.washingMachines.map(machine => {
            return {
              ...machine,
              ...(machineMap[machine.id] ?? {}),
              state: machineMap[machine.id]?.state || machine.state,
              reservedFor: machineMap[machine.id]?.reservedFor,
            };
          }),
          dryers: shopDetailsQuery.data.machines.dryers.map(machine => ({
            ...machine,
            ...(machineMap[machine.id] ?? {}),
            state: machineMap[machine.id]?.state || machine.state,
            reservedFor: machineMap[machine.id]?.reservedFor,
          })),
        },
      },
    };
  }, [
    machineStatusList.data,
    shopDetailsQuery.data,
    shopDetailsQuery.status,
    shopDetailsQuery.isPending,
    isMachineStatesPending,
  ]);
}
